import { z } from 'zod';
/**
 * Was jemand an der Plattform tun darf (ADR 0042).
 *
 * **Additiv, wie im Mandanten.** Ein Plattformbenutzer trägt eine Liste
 * davon, jede Rolle bringt ihre Regeln mit, und wer mehrere trägt, darf die
 * Vereinigung. `platform_admin` ist keine Stufe über den anderen, sondern die
 * Rolle, deren Regeln zufällig alles abdecken.
 *
 * Benannt nach dem Arbeitsgebiet, nicht nach dem Rang: `platform_finance` und
 * weitere reihen sich später daran an. Der Vertrieb ist keine Plattformrolle
 * mehr — er arbeitet im Sales-Add-on seines eigenen Partner-Tenants
 * (SALES-68). Das Präfix unterscheidet
 * sie von den Mandantenrollen (`system_admin`, `org_manager`, …), die im
 * selben Code liegen.
 */ export const PlatformRoleSchema = z.enum([
    'platform_admin',
    'platform_support'
]);
export const PLATFORM_ROLES = PlatformRoleSchema.options;
/**
 * Woran an der Plattform gearbeitet wird — der CASL-Gegenstand jedes
 * Superadmin-Endpunkts.
 *
 * Nach Ressource geschnitten, nicht nach Navigationsgruppe: erst damit kann
 * eine Rolle anlegen dürfen, ohne löschen zu dürfen (least privilege).
 */ export const PlatformSubjectSchema = z.enum([
    /** Konten und die HubSpot-Firmen dahinter. */ 'PlatformAccount',
    /** Arbeitsbereiche; Details (`readSensitive`) und Klonen (`manage`) eigens. */ 'PlatformTenant',
    /** Die Menschen in einem Kunden-Arbeitsbereich. */ 'PlatformTenantUser',
    /** Apps je Arbeitsbereich: freischalten, sperren, konfigurieren. */ 'PlatformTenantApp',
    /** Das Plattformpersonal selbst. */ 'PlatformUser',
    /** Material und News für Partner. */ 'PlatformPartnerContent',
    /** Protokoll, Analytics, KI-Nutzung — nur lesend. */ 'PlatformInsights',
    /** Plattformweite Einstellungen. */ 'PlatformSettings'
]);
/**
 * Die Regeln je Rolle — die eine Tabelle, aus der Backend (CASL) und
 * Frontend (Navigation, Knöpfe) lesen. Zwei Kopien davon liefen auseinander.
 *
 * `platform_admin` bekommt die Wildcard statt einer Aufzählung: eine Liste
 * müsste bei jeder neuen Ressource mitwachsen, und genau das vergisst man.
 * Der Support liest, er ändert nichts und rührt die Plattform nicht an.
 */ export const PLATFORM_ROLE_RULES = {
    platform_admin: [
        {
            action: 'manage',
            subject: 'all'
        }
    ],
    platform_support: [
        {
            action: 'read',
            subject: 'PlatformAccount'
        },
        // Die Liste, nicht die Details: der Support sieht, DASS es den
        // Arbeitsbereich gibt, und wer ihn hat.
        {
            action: 'read',
            subject: 'PlatformTenant'
        },
        {
            action: 'read',
            subject: 'PlatformInsights'
        }
    ]
};
/** Die Regeln einer Rollenliste, vereinigt. */ export function platformRulesFor(roles) {
    return (roles ?? []).flatMap((role)=>PLATFORM_ROLE_RULES[role] ?? []);
}
/**
 * Ob diese Rollen das dürfen — mit CASLs Bedeutung: `manage` deckt jede
 * Aktion, `all` jeden Gegenstand. Für das Frontend; das Backend baut aus
 * denselben Regeln eine CASL-Ability.
 */ export function platformCan(roles, action, // `all` fragt nach der Wildcard selbst: das darf nur, wer alles darf.
subject) {
    return platformRulesFor(roles).some((rule)=>(rule.action === 'manage' || rule.action === action) && (rule.subject === 'all' || rule.subject === subject));
}

//# sourceMappingURL=platform-role.js.map